import { Div, Flex, Text } from '@sendoutcards/quantum-design-ui'
import React from 'react'

import { Button } from 'src/design_system/components/Button/Button'
import {
  useAccount,
  useContainerWidth,
  useState,
  useEffect,
  useCallback,
} from 'src/hooks'

type GratitudeMovementWidgetType = {
  title: string
  isMobile?: boolean
  isTablet?: boolean
}

const GRATITUDE_MOVEMENT_IMAGE_URL =
  'https://soc-website-public.s3.us-west-2.amazonaws.com/static/soc-website/images/manual/GMopp-Thumbnail600.png'

export const GratitudeMovementWidget: React.FC<GratitudeMovementWidgetType> = ({
  title,
  isMobile,
  isTablet,
}) => {
  const [containerRef] = useContainerWidth()

  const account = useAccount()
  const presentationUrl = `https://promptings.com/presentation?sponsor=${account.genealogyId}`

  const resetShareLabelTimeoutRef = React.useRef<number | null>(null)
  const [shareButtonLabel, setShareButtonLabel] = useState('Share Presentation')

  useEffect(() => {
    return () => {
      if (resetShareLabelTimeoutRef.current != null) {
        window.clearTimeout(resetShareLabelTimeoutRef.current)
      }
    }
  }, [])

  const showCopiedFeedback = useCallback(() => {
    setShareButtonLabel('Copied!')
    if (resetShareLabelTimeoutRef.current != null) {
      window.clearTimeout(resetShareLabelTimeoutRef.current)
    }
    resetShareLabelTimeoutRef.current = window.setTimeout(() => {
      setShareButtonLabel('Share Presentation')
    }, 2000)
  }, [])

  const handleShare = useCallback(async () => {
    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.share === 'function'
    ) {
      try {
        await navigator.share({
          title: 'Promptings Presentation',
          url: presentationUrl,
        })
      } catch {
        // Ignore (e.g. user canceled) and fall back to copy.
      }
    }

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(presentationUrl)
        showCopiedFeedback()
        return
      }
    } catch {
      // Fall through to prompt fallback.
    }

    window.prompt('Copy this link:', presentationUrl)
  }, [presentationUrl, showCopiedFeedback])

  return (
    <Flex
      width="100%"
      flexDirection="column"
      backgroundColor="#fff"
      alignItems="center"
      borderRadius="32px"
      minWidth="340px"
      height="inherit"
      inset="16px"
      style={{ boxShadow: 'rgba(0, 0, 0, 0.04) 0px 4px 32px' }}
    >
      <Flex width="100%" justifyContent="center" alignItems="center">
        <Flex>
          <div
            style={{
              width: '100%',
              borderRadius: '20px',
              cursor: 'pointer',
              overflow: 'hidden',
            }}
            onClick={() => window.open(presentationUrl, '_blank')}
          >
            <img
              src={GRATITUDE_MOVEMENT_IMAGE_URL}
              alt="Gratitude movement"
              style={{
                display: 'block',
                width: '100%',
                height: 'auto',
              }}
            />
          </div>
        </Flex>
      </Flex>
      <Div
        ref={containerRef}
        display="flex"
        width="100%"
        inset="16px"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        <Text
          type="subtitle"
          weight="semiBold"
          alignment="left"
          content={title}
        />
      </Div>
      <Flex
        columnGap="x2"
        rowGap="x2"
        width="100%"
        flexWrap="wrap-reverse"
        inset="16px"
      >
        <Flex width="100%" justifyContent="center" alignItems="center">
          <Button
            title={'Go to Presentation'}
            background="#424448"
            textColor="#FFF"
            whiteSpace="nowrap"
            textSize="14px"
            width="100%"
            onClick={() => window.open(presentationUrl, '_blank')}
          />
        </Flex>
        <Flex width="100%" justifyContent="center" alignItems="center">
          <Button
            title={shareButtonLabel}
            backgroundColor="#F2F2F2"
            backgroundColorHover="rgba(126, 34, 206, 0.08)"
            icon={{ name: 'deviceShare', size: 16, color: '#424448' }}
            textColor="#424448"
            textSize="14px"
            whiteSpace="nowrap"
            width="100%"
            hoverStyle={{
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 16px rgba(126, 34, 206, 0.12)',
            }}
            onClick={handleShare}
          />
        </Flex>
      </Flex>
    </Flex>
  )
}
